import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { openPrintWindow } from "@/utils/printHelper";
import {
  ArrowLeft,
  Edit,
  Loader2,
  Package,
  Ruler,
  Layers,
  Palette,
  DollarSign,
  MessageSquare,
  X,
  Image,
  Printer,
  QrCode,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProdMaterialDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [spec, setSpec] = useState(null);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const qrRef = useRef(null);
  const [newComment, setNewComment] = useState("");

  useEffect(() => {
    fetchSpecification();
  }, [id]);

  const fetchSpecification = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/product-specifications/${id}`);
      setSpec(response.data);
    } catch (error) {
      console.error("Error fetching specification:", error);
      toast.error("Failed to load product specification");
      navigate("/workorder/prod-material");
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    try {
      await axios.post(`${API}/product-specifications/${id}/comments`, {
        text: newComment,
      });
      toast.success("Comment added");
      setNewComment("");
      fetchSpecification();
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(
        `${API}/product-specifications/${id}/comments/${commentId}`
      );
      toast.success("Comment deleted");
      fetchSpecification();
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    }
  };

  const printQRCode = () => {
    const printContent = qrRef.current;
    if (!printContent || !spec) return;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Code - ${spec.product_id}</title>
        <style>
          body {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            margin: 0;
            font-family: Arial, sans-serif;
          }
          .qr-container {
            text-align: center;
            padding: 20px;
          }
          .product-info {
            margin-bottom: 15px;
          }
          .product-id {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .product-name {
            font-size: 14px;
            color: #666;
          }
          .qr-code {
            margin: 20px 0;
          }
          .scan-text {
            font-size: 12px;
            color: #888;
            margin-top: 10px;
          }
          @media print {
            body { margin: 0; }
          }
        </style>
      </head>
      <body>
        <div class="qr-container">
          <div class="product-info">
            <div class="product-id">${spec.product_id}</div>
            <div class="product-name">${spec.item_name}</div>
          </div>
          <div class="qr-code">
            ${printContent.innerHTML}
          </div>
          <div class="scan-text">Scan to view product image</div>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    openPrintWindow(htmlContent);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Loader2 className="h-12 w-12 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (!spec) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <p className="text-gray-400">Product specification not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/workorder/prod-material")}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8 text-cyan-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {spec.product_id}
                </h1>
                <p className="text-gray-400 text-sm">{spec.company_name}</p>
              </div>
            </div>
          </div>
          <Button
            data-testid="edit-from-detail-btn"
            onClick={() => navigate("/workorder/prod-material", { state: { editId: spec.id } })}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image URL & QR Code Card - Only show if image_link exists */}
            {spec.image_link && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="border-b border-slate-700 flex flex-row items-center justify-between">
                  <CardTitle className="text-lg text-cyan-400 flex items-center gap-2">
                    <Image className="h-5 w-5" />
                    Image URL
                  </CardTitle>
                  <Button
                    data-testid="print-qr-detail-btn"
                    onClick={() => setShowQRDialog(true)}
                    size="sm"
                    className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  >
                    <QrCode className="h-4 w-4 mr-1" />
                    Print QR
                  </Button>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-gray-300 text-sm break-all">{spec.image_link}</p>
                </CardContent>
              </Card>
            )}

            {/* Basic Info Card */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="border-b border-slate-700">
                <CardTitle className="text-lg text-cyan-400 flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Product Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-400 text-sm">Product ID</Label>
                    <p className="text-white font-medium mt-1">
                      {spec.product_id}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Company Name</Label>
                    <p className="text-white font-medium mt-1">
                      {spec.company_name}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-400 text-sm">Item Name</Label>
                    <p className="text-white font-medium mt-1">
                      {spec.item_name}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dimensions Card */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="border-b border-slate-700">
                <CardTitle className="text-lg text-cyan-400 flex items-center gap-2">
                  <Ruler className="h-5 w-5" />
                  Dimensions & Sizes
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <Label className="text-gray-400 text-sm">Length</Label>
                    <p className="text-white font-medium mt-1">
                      {spec.length ? `${spec.length} mm` : "-"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Breadth</Label>
                    <p className="text-white font-medium mt-1">
                      {spec.breadth ? `${spec.breadth} mm` : "-"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Height</Label>
                    <p className="text-white font-medium mt-1">
                      {spec.height ? `${spec.height} mm` : "-"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Reel Size</Label>
                    <p className="text-white font-medium mt-1">
                      {spec.reel_size || "-"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Cutting Size</Label>
                    <p className="text-white font-medium mt-1">
                      {spec.cutting_size || "-"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* GSM Configuration Card */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="border-b border-slate-700">
                <CardTitle className="text-lg text-cyan-400 flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  GSM Configuration ({spec.ply}-Ply)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {spec.gsm_config && spec.gsm_config.length > 0 ? (
                  <div className="grid grid-cols-3 gap-4">
                    {spec.gsm_config.map((item, index) => (
                      <div
                        key={index}
                        className="p-3 bg-slate-700/50 rounded-lg border border-slate-600"
                      >
                        <Label className="text-gray-400 text-sm">
                          {item.name}
                        </Label>
                        <p className="text-white font-medium mt-1">
                          {item.gsm || "-"} GSM
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400">No GSM configuration</p>
                )}
              </CardContent>
            </Card>

            {/* Additional Details Card */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="border-b border-slate-700">
                <CardTitle className="text-lg text-cyan-400 flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Additional Details
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-400 text-sm">Printing</Label>
                    <p className="text-white font-medium mt-1">
                      {spec.printing_enabled || spec.printing || "-"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Stitching</Label>
                    <p className="text-white font-medium mt-1">
                      {spec.stitching || "-"}
                    </p>
                  </div>
                  {(spec.printing_enabled === "Yes" || spec.printing === "Yes") && (
                    <>
                      <div>
                        <Label className="text-gray-400 text-sm">Colors</Label>
                        <p className="text-white font-medium mt-1">
                          {spec.colors?.length > 0 
                            ? spec.colors.join(", ") 
                            : (spec.color || "-")}
                        </p>
                      </div>
                      <div>
                        <Label className="text-gray-400 text-sm">No of Colors</Label>
                        <p className="text-white font-medium mt-1">
                          {spec.no_of_colors || "-"}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Rate Card */}
            <Card className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border-cyan-500/30">
              <CardHeader className="border-b border-cyan-500/30">
                <CardTitle className="text-lg text-cyan-400 flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Rate
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-white">
                  {spec.rate ? `₹${spec.rate.toFixed(2)}` : "-"}
                </p>
              </CardContent>
            </Card>

            {/* Comments Card */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="border-b border-slate-700">
                <CardTitle className="text-lg text-purple-400 flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Comments ({spec.comments?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                {/* Existing comments */}
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {spec.comments && spec.comments.length > 0 ? (
                    spec.comments.map((comment) => (
                      <div
                        key={comment.id}
                        className="p-3 bg-slate-700/50 rounded-lg border border-slate-600"
                      >
                        <div className="flex justify-between items-start">
                          <p className="text-gray-300 text-sm flex-1">
                            {comment.text}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-red-400 hover:text-red-300 ml-2 h-6 w-6 p-0"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-gray-500 text-xs mt-1">
                          {new Date(comment.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-2">
                      No comments yet
                    </p>
                  )}
                </div>

                {/* Add new comment */}
                <div className="space-y-2 pt-2 border-t border-slate-700">
                  <Textarea
                    data-testid="detail-comment-input"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white text-sm"
                    placeholder="Add a comment..."
                    rows={2}
                  />
                  <Button
                    data-testid="detail-add-comment-btn"
                    onClick={handleAddComment}
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold text-sm"
                    size="sm"
                  >
                    Add Comment
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Timestamps */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-6 space-y-2">
                <div>
                  <Label className="text-gray-400 text-xs">Created</Label>
                  <p className="text-gray-300 text-sm">
                    {new Date(spec.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Last Updated</Label>
                  <p className="text-gray-300 text-sm">
                    {new Date(spec.updated_at).toLocaleString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* QR Code Print Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-xl text-cyan-400 flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              QR Code for Image Link
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Scan this QR code to access the product image
            </DialogDescription>
          </DialogHeader>

          <div className="py-6 flex flex-col items-center">
            <div className="bg-white p-4 rounded-lg" ref={qrRef}>
              {spec?.image_link && (
                <QRCodeSVG 
                  value={spec.image_link} 
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              )}
            </div>
            <div className="mt-4 text-center">
              <p className="text-white font-bold">{spec?.product_id}</p>
              <p className="text-gray-400 text-sm">{spec?.item_name}</p>
            </div>
            <p className="text-xs text-gray-500 mt-2 max-w-xs text-center break-all">
              {spec?.image_link}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQRDialog(false)} className="border-slate-600 text-gray-300">
              Close
            </Button>
            <Button
              data-testid="print-qr-confirm-btn"
              onClick={printQRCode}
              className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print QR Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProdMaterialDetail;
